import type { ReactNode } from "react";
import type { Application } from "../api";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
import { formatDate, initials } from "../presentation";
import { Button, IconButton } from "./ui/Button";
import { Icon } from "./ui/Icon";

export function ApplicationSelector({ applications, selected, onSelect }: {
  readonly applications: readonly Application[];
  readonly selected: Application | null;
  readonly onSelect: (application: Application) => void;
}) {
  return (
    <label className="management-app-selector">
      <span>当前应用</span>
      <select
        aria-label="切换当前应用"
        value={selected?.appId ?? ""}
        onChange={(event) => {
          const application = applications.find((candidate) => candidate.appId === event.currentTarget.value);
          if (application !== undefined) onSelect(application);
        }}
      >
        {applications.map((application) => (
          <option key={application.appId} value={application.appId}>{application.name} · {application.appKey}</option>
        ))}
      </select>
      <Icon name="chevron-down" size={15} />
    </label>
  );
}

export function ManagementHeading({ title, description, applications, selected, onSelect, action }: {
  readonly title: string;
  readonly description: string;
  readonly applications: readonly Application[];
  readonly selected: Application | null;
  readonly onSelect: (application: Application) => void;
  readonly action?: ReactNode;
}) {
  return (
    <section className="management-heading">
      <div>
        <p className="breadcrumb"><span>应用详情</span><Icon name="chevron-right" size={14} />运营与开发</p>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <div className="management-heading-actions">
        <ApplicationSelector applications={applications} selected={selected} onSelect={onSelect} />
        {action}
      </div>
    </section>
  );
}

export function PageState({ loading, error, empty, filtered, noun, onRetry, children }: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly empty: boolean;
  readonly filtered: boolean;
  readonly noun: string;
  readonly onRetry: () => void;
  readonly children: ReactNode;
}) {
  if (loading) return <div className="management-state" aria-live="polite"><span className="loader" /><strong>正在加载{noun}</strong><p>正在从应用级管理投影读取数据…</p></div>;
  if (error !== null) return <div className="management-state error-state" role="alert"><span className="empty-state-icon"><Icon name="warning" /></span><strong>{noun}加载失败</strong><p>{error}</p><Button onClick={onRetry}>重新加载</Button></div>;
  if (empty) return <div className="management-state"><span className="empty-state-icon"><Icon name="inbox" /></span><strong>{filtered ? "没有符合条件的数据" : `还没有${noun}`}</strong><p>{filtered ? "调整搜索关键词或筛选条件后重试。" : `创建或通过 IM 接口写入后，${noun}会显示在这里。`}</p></div>;
  return children;
}

export function PaginationControls({ pageNo, pageSize, total, hasMore, onChange }: {
  readonly pageNo: number;
  readonly pageSize: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly onChange?: (page: number) => void;
}) {
  const start = total === 0 ? 0 : (pageNo - 1) * pageSize + 1;
  const end = Math.min(pageNo * pageSize, total);
  return (
    <footer className="management-pagination">
      <span>第 {start}–{end} 条，共 {total} 条</span>
      <div>
        <IconButton disabled={pageNo <= 1} icon="chevron-left" label="上一页" onClick={() => { onChange?.(pageNo - 1); }} />
        <span>第 {pageNo} 页</span>
        <IconButton disabled={!hasMore} icon="chevron-right" label="下一页" onClick={() => { onChange?.(pageNo + 1); }} />
      </div>
    </footer>
  );
}

export function StatusTag({ status, kind = "entity" }: { readonly status: number | string; readonly kind?: "entity" | "message" }) {
  if (kind === "message") return <span className="management-tag tag-success">已接受</span>;
  const config = status === 1
    ? { label: "正常", className: "tag-success" }
    : status === 2
      ? { label: "已停用", className: "tag-danger" }
      : { label: "不可用", className: "tag-muted" };
  return <span className={`management-tag ${config.className}`}>{config.label}</span>;
}

export function CopyText({ value, label }: { readonly value: string; readonly label: string }) {
  const { copied, copy } = useCopyFeedback(value);
  return (
    <button
      aria-label={copied ? `${label}已复制` : `复制${label}`}
      className="management-copy"
      title={copied ? "已复制" : `复制${label}`}
      type="button"
      onClick={() => { void copy(); }}
    >
      <code>{value}</code>
      <Icon name={copied ? "check" : "copy"} size={14} />
      {copied ? <span className="management-copy-feedback" role="status">已复制</span> : null}
    </button>
  );
}

export function UserIdentity({ userId, displayName, avatarUrl }: {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
}) {
  return <span className="management-identity">{avatarUrl === null ? <span>{initials(displayName)}</span> : <img alt="" src={avatarUrl} />}<span><strong>{displayName}</strong><small>{userId}</small></span></span>;
}

export function TimeValue({ value }: { readonly value: number | null }) {
  return value === null ? <span className="muted-value">暂无记录</span> : <time dateTime={new Date(value).toISOString()} title={new Date(value).toLocaleString()}>{formatDate(value)}</time>;
}
